import { useApp, useBehavior, useSubscribeOnWsUpdate, useUpdateWidget } from '@libs/hooks';
import { ActionActionTypeEnum, AssetsSvgMap, TWidget, TWidgetProps } from '@libs/types';
import { ConditionalRender, currencyFormat, eventBus, significantDecimalPlaces } from '@libs/utils';
import clsx from 'clsx';
import React, { Activity, useEffect, useMemo } from 'react';
import { SvgComponent } from '../../common/Svg/SvgComponent';
import { TBalanceData } from '../../types/data';
import { getInlineStyles } from '../../utils/styles';
import { TextCommon } from '../UI/Text/TextCommon';
import SelectBalanceBottomSheet from './SelectBalanceBottomSheet';
import styles from './styles/balance.module.css';
import { getIconForCurrencyIso } from './utils/balanceUtils';

export const Balance: React.FC<TWidgetProps<TBalanceData>> = ({ widget }) => {
    const { setPopup } = useApp();
    const { updateWidget } = useUpdateWidget(widget.uid);
    const { isHidden } = useBehavior(widget);
    const data = widget.data ?? {};

    const currentBalance = useMemo(() => {
        if (Array.isArray(data.balances) && data.balances?.length > 0) {
            const currentBalance = data.balances.filter((b) => b?.id === data.value)[0] ?? data.balances[0];
            return {
                currentBalance: currentBalance,
                showArrow: data.balances.length > 1 || data.canGetPaysendCard || data.canExchange || data.canAddCurrency,
            };
        }
        return null;
    }, [data.balances, data.canAddCurrency, data.canExchange, data.canGetPaysendCard, data.value]);

    const arrActions = useMemo(() => widget.actions?.find((a) => a?.actionType === ActionActionTypeEnum.onTimer), [widget.actions]);

    useSubscribeOnWsUpdate(arrActions);

    useEffect(() => {
        if (widget.internalId) {
            eventBus.subscribeWS(widget.internalId, (w) => {
                if (w) {
                    updateWidget(w as unknown as TWidget<TBalanceData>);
                }
            });
        }
        return () => {
            eventBus.unsubscribeWsSpecificWidget(widget.internalId);
        };
    }, [updateWidget, widget.internalId]);

    const cb = currentBalance?.currentBalance;
    const hasCurrencyIcon = !!getIconForCurrencyIso(cb?.iso);

    if (cb) {
        const decimalLength = significantDecimalPlaces(cb.balance) > 0 ? 2 : 0;
        return (
            <Activity mode={isHidden ? 'hidden' : 'visible'}>
                <div data-test={widget.uid} className={styles.balanceContainer} style={getInlineStyles({ margin: widget.options?.margin })}>
                    <div
                        className={clsx(styles.sumContainer, currentBalance.showArrow && styles.showCursor)}
                        key={cb.id}
                        onClick={() => {
                            if (currentBalance.showArrow) {
                                setPopup(<SelectBalanceBottomSheet value={cb.id} widget={widget} />);
                            }
                        }}
                    >
                        <div data-test={widget.uid + '_amount'} className={styles.sum}>
                            <ConditionalRender condition={hasCurrencyIcon}>
                                <TextCommon
                                    isWidget={false}
                                    content={currencyFormat(cb.balance, cb.iso, decimalLength) as string}
                                    options={{
                                        maxLines: 1,
                                        enableAutoResize: true,
                                        variant: 'display1',
                                        textColor: 'black',
                                    }}
                                />
                            </ConditionalRender>
                            <ConditionalRender condition={!hasCurrencyIcon}>
                                <TextCommon
                                    isWidget={false}
                                    content={`${cb.balance} ${cb.iso?.toLowerCase()}`}
                                    options={{
                                        maxLines: 1,
                                        enableAutoResize: true,
                                        variant: 'display1',
                                        textColor: 'black',
                                    }}
                                    textTransformation={[
                                        {
                                            text: `${cb.balance}`,
                                            precision: decimalLength,
                                            useLocaleNumberFormat: true,
                                        },
                                        {
                                            text: cb.iso?.toLowerCase(),
                                            smallCaps: true,
                                        },
                                    ]}
                                />
                            </ConditionalRender>
                        </div>
                        <div data-test={widget.uid + '_currency'} className={styles.pseudoDropdown}>
                            <TextCommon isWidget={false} content={cb.name} options={{ variant: 'secondary', textColor: 'grey_72_lc' }} />
                            {currentBalance.showArrow && <SvgComponent width={16} height={16} name={AssetsSvgMap.ic_chevron_down_small} fill="grey_72_lc" />}
                        </div>
                    </div>
                </div>
            </Activity>
        );
    } else {
        return false;
    }
};
